import * as Common from '@/libs/Common';
import * as DateTimeHelper from '@/libs/DateTimeHelper';
import * as ApiClient from '@/libs/ApiClient';
import router from '@/router';
import { RouteUrlList, type IRouteUrlListElement } from '@/libs/RouteList';
import API from '@/api/v1/ServiceAlert';
import type { AxiosInstance } from 'axios';
import { ServiceAlertApiClient } from '@/libs/ServiceAlertApiClient';
import { useClientStore } from '@/stores/modules/clientInfo';
import { useEsunMB } from '@/composables/useEsunMB';

import { SimplePopUp, SimplePopUpBtnType, SimplePopUpModalType, SimplePopUpSize } from '@/libs/SimplePopUp';
const simplePopUp = new SimplePopUp();
export enum ServiceAlertTypeEnum {
    NoMessage = 0, // 無訊息
    Warning = 1, // 有彈窗提示
    Block = 2 // 阻擋進入
}

interface IServiceAlert {
    ServiceAlertApiClient: AxiosInstance;
    AlertType: ServiceAlertTypeEnum;
    AlertTitle: string | null;
    AlertMessage: string | null;

    SkipIdleCheck: boolean;
    ShowIdleTimeoutHint: boolean;
    IdleTimeoutHintMinutes: number;

    IdleCheckTimeoutId: number | undefined;
    IdleRedirectCountDownSec: number;

    RedirectUrl: IRouteUrlListElement;
    IsInWebView: boolean;

    /**
     * 取得停登訊息
     * @returns
     */
    GetAlertMessage(): Promise<void> | undefined;

    /**
     * 閒置倒數 偵測(for 9分鐘逾時提醒第10分鐘後登出)
     * @param path
     */
    IdletimeoutDetection(): Promise<void>;

    /**
     * 顯示 停登訊息
     */
    ShowAlertMessage(isDisplay: boolean): Promise<boolean>;
}

class ServiceAlert implements IServiceAlert {
    ServiceAlertApiClient: AxiosInstance; // 避免循環參照不用 Api.Instance
    RequestToken: string | null; // XSRF Token
    AlertHash: string | null; // Alert message md5 版號
    AlertType: ServiceAlertTypeEnum; // 停登類型
    AlertTitle: string | null; // 停登標題
    AlertMessage: string | null; // 停登訊息

    SkipIdleCheck: boolean; // 特殊頁面不進行閒置逾時偵測
    ShowIdleTimeoutHint: boolean; // 踢退時是否顯示逾時提示 (完成申請頁、申請進度查看頁 不顯示繼續提示)
    IdleTimeoutHintMinutes: number; // 閒置多久 跳提醒

    IdleCheckTimeoutId: number | undefined;
    IsShowingIdleHint: boolean; // 防止重複顯示閒置提示彈窗
    IdleRedirectCountDownSec: number;

    RedirectUrl: IRouteUrlListElement;
    IsInWebView: boolean;

    constructor(redirectUrl: IRouteUrlListElement = RouteUrlList.StateError, isInWebView: boolean = false) {
        this.ServiceAlertApiClient = ServiceAlertApiClient;

        this.RequestToken = '';
        this.AlertHash = '';
        this.AlertType = ServiceAlertTypeEnum.NoMessage;
        this.AlertTitle = null;
        this.AlertMessage = null;

        this.SkipIdleCheck = false;
        this.ShowIdleTimeoutHint = true;
        this.IdleTimeoutHintMinutes = 2;

        this.IdleCheckTimeoutId = undefined;
        this.IsShowingIdleHint = false;
        this.IdleRedirectCountDownSec = 60;
        this.RedirectUrl = redirectUrl;
        this.IsInWebView = isInWebView;
    }

    /**
     * 取得停登訊息
     * @returns
     */
    GetAlertMessage(): Promise<void> | undefined {
        try {
            const appSelf = this;
            const config = { params: { showOverlay: ApiClient.paramTypeEnum.false } };
            return API.GetAlertMessage()
                .then((res) => {
                    if (res.status) {
                        appSelf.RequestToken = typeof res.requestToken !== 'undefined' ? res.requestToken : '';
                        appSelf.AlertHash = typeof res.alertHash !== 'undefined' ? res.alertHash : '';
                        appSelf.AlertType = typeof res.alertType !== 'undefined' ? res.alertType : ServiceAlertTypeEnum.NoMessage;
                        appSelf.AlertTitle = typeof res.alertTitle !== 'undefined' ? res.alertTitle : '';
                        appSelf.AlertMessage = typeof res.alertMessage !== 'undefined' ? res.alertMessage : '';
                        appSelf.ShowIdleTimeoutHint = typeof res.isShowAlertMessage !== 'undefined' ? res.isShowAlertMessage : false;
                        appSelf.IdleTimeoutHintMinutes = typeof res.idleTimeoutHintMinutes !== 'undefined' ? res.idleTimeoutHintMinutes : 9;

                        if (appSelf.AlertHash !== null) {
                            const clinetStore = useClientStore();
                            clinetStore.setCurrentAlertId(appSelf.AlertHash);
                        }
                    } else {
                        ApiClient.HandleGeneralRespError(res);
                    }
                })
                .catch((error) => {
                    throw error;
                });
        } catch (e: any) {
            Common.ConsoleLog(e);
        }
    }

    /**
     * 閒置倒數 偵測(for 9分鐘逾時提醒第10分鐘後登出)
     * @param path
     */
    async IdletimeoutDetection(): Promise<void> {
        const clinetStore = useClientStore();
        if (clinetStore.routeInitState) {
            Common.ConsoleLog('[ServiceAlert.IdletimeoutDetection] 已初始化，開始閒置逾時偵測');

            if (clinetStore.isLogin.valueOf()) {
                await this.StartIdleDetect();
            }
        } else {
            const IsLoggin = await API.ServiceAlertLoginCheck();
            if (IsLoggin) {
                this.StartIdleDetect();
            }
        }
    }

    /**
     * 顯示 停登訊息
     */
    async ShowAlertMessage(isDisplay: boolean): Promise<boolean> {
        const appSelf = this;
        // 是否已確認過該次alertId
        const clinetStore = useClientStore();
        const isConfirmAlert =
            clinetStore.currentAlertId && clinetStore.confirmedAlertId && clinetStore.currentAlertId === clinetStore.confirmedAlertId;
        if (this.AlertType == ServiceAlertTypeEnum.Warning && isDisplay && !isConfirmAlert) {
            return simplePopUp
                .showAlert(
                    SimplePopUpModalType.Warning,
                    this.AlertTitle,
                    this.AlertMessage,
                    [
                        {
                            id: 'confirm',
                            type: SimplePopUpBtnType.callback,
                            btnClass: 'btn',
                            resolveParam: 'confirm',
                            title: '確認'
                        }
                    ],
                    SimplePopUpSize.lg,
                    true
                )
                .then((result: string) => {
                    appSelf.ConfirmReadAlert();
                    return true;
                });
        } else {
            return new Promise((resolve) => {
                resolve(true);
            });
        }
    }

    /**
     * @todo 確認停登訊息
     */
    ConfirmReadAlert() {
        // const url = this.ConfirmServiceAlert;
        if (this.AlertHash !== null) {
            const clinetStore = useClientStore();
            clinetStore.setConfirmedAlertId(this.AlertHash);
        }
        API.ConfirmServiceAlert().catch((error) => {
            ApiClient.HandleError(error);
        });
    }

    /**
     * @todo 開始逾時偵測
     */
    StartIdleDetect() {
        const appSelf = this;

        // 防止重複執行：如果正在顯示閒置提示彈窗，則跳過
        if (this.IsShowingIdleHint) {
            Common.ConsoleLog('[ServiceAlert.StartIdleDetect] 正在顯示閒置提示，跳過重複呼叫');
            return;
        }

        // 清除舊的 timeout
        if (this.IdleCheckTimeoutId) {
            Common.ConsoleLog(`[ServiceAlert.StartIdleDetect] 清除舊的 TimeoutId: ${this.IdleCheckTimeoutId}`);
            clearTimeout(this.IdleCheckTimeoutId);
            this.IdleCheckTimeoutId = undefined;
        }

        const currentData: Date = new Date();
        const alertDate = new Date(currentData.getTime() + this.IdleTimeoutHintMinutes * 60 * 1000);
        this.IdleCheckTimeoutId = setTimeout(
            () => {
                appSelf.IdleTimeOutHint();
            },
            this.IdleTimeoutHintMinutes * 60 * 1000
        );
        Common.ConsoleLog(
            `[ServiceAlert.StartIdleDetect] 設置新的 TimeoutId: ${this.IdleCheckTimeoutId},逾時提示將於:${DateTimeHelper.GetFormatDateTimeString(alertDate)}`
        );
    }

    /**
     * @todo 清除idletimeout id
     */
    ClearIdleCheckTimeoutId() {
        Common.ConsoleLog(`[ServiceAlert.ClearIdleCheckTimeoutId] Clear TimeoutId: ${this.IdleCheckTimeoutId}`);
        if (this.IdleCheckTimeoutId) {
            clearTimeout(this.IdleCheckTimeoutId);
            this.IdleCheckTimeoutId = undefined;
        }
        // 注意：不在這裡重置 IsShowingIdleHint，由 IdleTimeOutHint 的結果處理
    }

    /**
     * @todo 逾時踢退彈窗顯示
     */
    IdleTimeOutHint() {
        const appSelf = this;

        // 防止重複顯示：如果已經在顯示彈窗，則跳過
        if (this.IsShowingIdleHint) {
            Common.ConsoleLog('[ServiceAlert.IdleTimeOutHint] 已在顯示閒置提示，跳過重複呼叫');
            return;
        }

        // 設置標誌，表示正在顯示彈窗
        this.IsShowingIdleHint = true;
        Common.ConsoleLog('[ServiceAlert.IdleTimeOutHint] 開始顯示閒置提示彈窗');

        simplePopUp
            .showAlert(
                SimplePopUpModalType.Warning,
                '網頁閒置過久',
                `您在本網頁已閒置超過<b class="text-orange">${appSelf.IdleTimeoutHintMinutes}</b>分鐘，本網頁將於<b class="timer text-orange" ></b>後，
          自動離開本服務，若您需繼續使用，請按「我要繼續操作」。`,
                [
                    {
                        id: 'confirm',
                        type: SimplePopUpBtnType.callback,
                        btnClass: 'btn',
                        resolveParam: 'confirm',
                        title: '我要繼續操作'
                    }
                ],
                SimplePopUpSize.lg,
                false,
                undefined,
                appSelf.IdleRedirectCountDownSec
            )
            .then((result: string) => {
                // 重置標誌，允許下次顯示
                appSelf.IsShowingIdleHint = false;
                Common.ConsoleLog('[ServiceAlert.IdleTimeOutHint] 彈窗已關閉，重置標誌');

                if (result === 'confirm') {
                    appSelf.ConfirmIdleAlert();
                } else {
                    appSelf.CancelAndRedirect();
                }
            })
            .catch((e: any) => {
                // 重置標誌，允許下次顯示
                appSelf.IsShowingIdleHint = false;
                Common.ConsoleLog('[ServiceAlert.IdleTimeOutHint] 彈窗發生錯誤，重置標誌');
                appSelf.CancelAndRedirect();
            });
    }

    /**
     * @todo 延展逾時倒數
     */
    ConfirmIdleAlert() {
        const appSelf = this;
        return API.ConfirmServiceAlert()
            .then((res) => {
                if (res && res.status) {
                    appSelf.StartIdleDetect();
                } else {
                    ApiClient.HandleGeneralRespError(res);
                }
            })
            .catch(function (error) {
                ApiClient.HandleError(error);
            });
    }

    /**
     * @todo 逾時(倒數結束未點選繼續、確認逾時)，不顯示Api < 200、no response錯誤
     */
    CancelAndRedirect() {
        const redirectUrl = this.RedirectUrl;
        const isInWebView = this.IsInWebView;
        const { closeWebView } = useEsunMB();
        return API.IdleTimeout()
            .then(() => {
                if (isInWebView) {
                    closeWebView();
                } else {
                    router.replace({
                        name: redirectUrl.name,
                        query: { readlocally: 'y' }
                    });
                }
            })
            .catch((error) => {
                if (isInWebView) {
                    closeWebView();
                } else {
                    router.replace({
                        name: redirectUrl.name,
                        query: { readlocally: 'y' }
                    });
                }
            });
    }
}

export { ServiceAlertApiClient, ServiceAlert };
